/**
 * Lead Enrichment Management (Phase 9G)
 * Queue management, bulk enrichment, status tracking, and retry capabilities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Sparkles, 
  RefreshCw, 
  Search, 
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Database,
  TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadEnrichmentManagement = ({ user }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [enriching, setEnriching] = useState({});
  const [stats, setStats] = useState({
    queued: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    not_started: 0
  });

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchEnrichmentStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/phase9/leads?limit=1000`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const allLeads = data.leads || [];
        
        setStats({
          queued: allLeads.filter(l => l.enrichment_status === 'queued').length,
          processing: allLeads.filter(l => l.enrichment_status === 'processing').length,
          // The enrichment worker writes "enriched"; "completed" is legacy.
          completed: allLeads.filter(l => l.enrichment_status === 'enriched' || l.enrichment_status === 'completed').length,
          failed: allLeads.filter(l => l.enrichment_status === 'failed').length,
          not_started: allLeads.filter(l => !l.enrichment_status || l.enrichment_status === 'not_started').length
        });
      }
    } catch (error) {
      console.error('Error fetching enrichment stats:', error);
    }
  }, [authHeaders]);

  const fetchLeadsByStatus = useCallback(async (status) => {
    setLoading(true);
    try {
      // The "completed" tab lists what the enrichment worker marks "enriched".
      const apiStatus = status === 'completed' ? 'enriched' : status;
      const response = await fetch(`${API}/phase9/leads?enrichment_status=${apiStatus}&limit=100&keyword=${encodeURIComponent(searchQuery)}`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, searchQuery]);

  useEffect(() => {
    fetchEnrichmentStats();
  }, [fetchEnrichmentStats]);

  useEffect(() => {
    if (activeTab !== 'overview') {
      const statusMap = {
        'queued': 'queued',
        'completed': 'completed',
        'failed': 'failed',
        'not_started': 'not_started'
      };
      fetchLeadsByStatus(statusMap[activeTab] || 'all');
    }
  }, [activeTab, searchQuery, fetchLeadsByStatus]);

  const handleEnrichLead = async (leadId) => {
    setEnriching((current) => ({ ...current, [leadId]: true }));
    try {
      const response = await fetch(`${API}/phase9/leads/${leadId}/enrich`, {
        method: 'POST',
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Lead enrichment started');
        await fetchEnrichmentStats();
        if (activeTab !== 'overview') {
          await fetchLeadsByStatus(activeTab);
        }
      } else {
        toast.error('Failed to enrich lead');
      }
    } catch (error) {
      toast.error('Error enriching lead');
    } finally {
      setEnriching((current) => ({ ...current, [leadId]: false }));
    }
  };

  const handleBulkEnrich = async () => {
    if (leads.length === 0) {
      toast.error('No leads to enrich');
      return;
    }

    try {
      const leadIds = leads.slice(0, 50).map(l => l.id); // Limit to 50 leads
      const response = await fetch(`${API}/phase9/leads/bulk-enrich`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ data: { lead_ids: leadIds } })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Bulk enrichment queued');
        await fetchEnrichmentStats();
        if (activeTab !== 'overview') {
          await fetchLeadsByStatus(activeTab);
        }
      } else {
        toast.error('Bulk enrichment failed');
      }
    } catch (error) {
      toast.error('Error during bulk enrichment');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'enriched':
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-300 border-green-500/30 flex items-center gap-1">
          <CheckCircle2 size={12} />
          Completed
        </Badge>;
      case 'queued':
        return <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 flex items-center gap-1">
          <Clock size={12} />
          Queued
        </Badge>;
      case 'processing':
        return <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 flex items-center gap-1">
          <RefreshCw size={12} className="animate-spin" />
          Processing
        </Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-300 border-red-500/30 flex items-center gap-1">
          <XCircle size={12} />
          Failed
        </Badge>;
      case 'skipped':
        return <Badge className="bg-white/10 text-white/60 border-white/20 flex items-center gap-1">
          <AlertCircle size={12} />
          Skipped (no website)
        </Badge>;
      default:
        return <Badge className="bg-white/10 text-white/60 border-white/20 flex items-center gap-1">
          <AlertCircle size={12} />
          Not Started
        </Badge>;
    }
  };

  const StatCard = ({ icon: Icon, label, value, color = 'text-white' }) => (
    <Card className="glass-panel border-white/10">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`h-12 w-12 rounded-full bg-white/10 flex items-center justify-center ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs text-white/60 uppercase tracking-wide">{label}</p>
            <strong className="text-2xl font-semibold text-white">{value || 0}</strong>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="page-stack" data-testid="lead-enrichment-management">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9G — Lead Enrichment Engine</p>
          <h2>Lead Enrichment Management</h2>
          <div className="hero-stats">
            <span>{stats.queued} Queued</span>
            <span>{stats.completed} Completed</span>
            <span>{stats.failed} Failed</span>
          </div>
        </div>
        <Button 
          className="primary-btn" 
          onClick={handleBulkEnrich}
          disabled={leads.length === 0 || activeTab === 'overview'}
          data-testid="bulk-enrich-btn"
        >
          <Sparkles size={16} />
          Bulk Enrich (Max 50)
        </Button>
      </section>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="glass-panel flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="queued">Queued ({stats.queued})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
          <TabsTrigger value="failed">Failed ({stats.failed})</TabsTrigger>
          <TabsTrigger value="not_started">Not Started ({stats.not_started})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <section className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard icon={AlertCircle} label="Not Started" value={stats.not_started} color="text-gray-400" />
            <StatCard icon={Clock} label="Queued" value={stats.queued} color="text-blue-400" />
            <StatCard icon={RefreshCw} label="Processing" value={stats.processing} color="text-yellow-400" />
            <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} color="text-green-400" />
            <StatCard icon={XCircle} label="Failed" value={stats.failed} color="text-red-400" />
          </section>

          <Card className="glass-panel border-white/10 mt-4">
            <CardHeader>
              <CardTitle className="text-white/90 flex items-center gap-2">
                <Database className="h-5 w-5 text-[var(--gold)]" />
                Enrichment Process
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-300 mb-2">How Enrichment Works</h4>
                  <ul className="space-y-2 text-xs text-blue-400/80">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <span>Enrichment adds missing data like website, reviews, social profiles, and business hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <span>Queued enrichment runs asynchronously to avoid API rate limits</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <span>All enrichment actions are logged to the lead activity timeline</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <span>Failed enrichments can be retried individually or in bulk</span>
                    </li>
                  </ul>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border border-white/10 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-[var(--gold)] mb-2" />
                    <h4 className="text-sm font-semibold text-white/90 mb-1">Success Rate</h4>
                    <p className="text-2xl font-bold text-[var(--gold)]">
                      {stats.completed + stats.failed > 0 
                        ? Math.round((stats.completed / (stats.completed + stats.failed)) * 100) 
                        : 0}%
                    </p>
                    <p className="text-xs text-white/60 mt-1">{stats.completed} of {stats.completed + stats.failed} completed</p>
                  </div>
                  <div className="p-4 border border-white/10 rounded-lg">
                    <Sparkles className="h-6 w-6 text-[var(--gold)] mb-2" />
                    <h4 className="text-sm font-semibold text-white/90 mb-1">Total Enriched</h4>
                    <p className="text-2xl font-bold text-white/90">{stats.completed}</p>
                    <p className="text-xs text-white/60 mt-1">Successfully enriched leads</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lead List Tabs */}
        {['queued', 'completed', 'failed', 'not_started'].map((status) => (
          <TabsContent key={status} value={status}>
            <Card className="glass-panel border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white/90">
                    {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} Leads
                  </CardTitle>
                  <Button 
                    className="ghost-btn"
                    onClick={() => fetchLeadsByStatus(status)}
                    data-testid="refresh-leads-btn"
                  >
                    <RefreshCw size={16} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-white/40" />
                  <Input
                    placeholder="Search leads..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>

                {loading ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full bg-white/10" />)}
                  </div>
                ) : leads.length === 0 ? (
                  <div className="text-center py-12">
                    <Database className="h-12 w-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/60">No leads found in this category</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {leads.map((lead) => (
                      <div 
                        key={lead.id} 
                        className="flex items-center justify-between p-4 border border-white/10 rounded-lg hover:bg-white/[0.02] transition-all"
                        data-testid={`enrichment-lead-${lead.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-sm font-semibold text-white/90">{lead.clinic_name}</h4>
                            {getStatusBadge(lead.enrichment_status)}
                          </div>
                          <p className="text-xs text-white/60">
                            {lead.city && lead.state ? `${lead.city}, ${lead.state}` : 'Location unknown'}
                          </p>
                          {lead.enrichment_queued_at && (
                            <p className="text-xs text-white/40 mt-1">
                              Queued: {new Date(lead.enrichment_queued_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {status === 'not_started' && (
                            <Button
                              className="ghost-btn text-xs"
                              onClick={() => handleEnrichLead(lead.id)}
                              disabled={enriching[lead.id]}
                              data-testid={`enrich-lead-${lead.id}`}
                            >
                              {enriching[lead.id] ? (
                                <>
                                  <RefreshCw size={14} className="animate-spin" />
                                  Queueing...
                                </>
                              ) : (
                                <>
                                  <Sparkles size={14} />
                                  Enrich
                                </>
                              )}
                            </Button>
                          )}
                          {status === 'failed' && (
                            <Button
                              className="ghost-btn text-xs"
                              onClick={() => handleEnrichLead(lead.id)}
                              disabled={enriching[lead.id]}
                              data-testid={`retry-enrich-${lead.id}`}
                            >
                              {enriching[lead.id] ? (
                                <>
                                  <RefreshCw size={14} className="animate-spin" />
                                  Retrying...
                                </>
                              ) : (
                                <>
                                  <RefreshCw size={14} />
                                  Retry
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default LeadEnrichmentManagement;
